<script setup lang="ts">
import {computed, ref, watch, nextTick} from 'vue';
import {getRandomId, roundTo} from '@/ts/utils/pure-functions';
import type {QuestionNumeric} from '@/ts/types/dto/interventions-dto';
import {
  type QuestionAnswerNumericBody,
  QuestionnaireType,
} from '@/ts/types/dto/session-request-dto';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import FormRequired from '@/vue/atoms/form-required.vue';
import {useUserStore} from '@/ts/store/user-store';
import {useUnitConversion} from '@/ts/composables/pure/use-unit-conversion';

const props = defineProps<{question: QuestionNumeric}>();
const model = defineModel<QuestionAnswerNumericBody | null>();

const {userData} = useUserStore();

const {isImperial, centimetersToUserUnit, userUnitToCentimeters} = useUnitConversion({
  countryCode: userData?.countryCode,
});

const heightCm = ref<string>('');
const heightFeet = ref<string>('');
const heightInches = ref<string>('');
const touched = ref<boolean>(false);

let internalUpdate = false;

const inputLabelledby = `question-${getRandomId()}`;

const HEIGHT_DECIMALS = 1;
const HEIGHT_STEP = 0.1;

const min = computed(() => props.question.numeric.minValue ?? 0);
const max = computed(() => props.question.numeric.maxValue ?? 400);

function setModel(cm: number | null): void {
  internalUpdate = true;
  if (cm === null) {
    model.value = null;
  } else {
    model.value = {numeric: cm, type: QuestionnaireType.NUMERIC};
  }
  void nextTick(() => {
    internalUpdate = false;
  });
}

function clampToRange(cm: number): number {
  return Math.min(Math.max(roundTo(cm, HEIGHT_DECIMALS), min.value), max.value);
}

/**
 * Normalize (feet, inches) to a canonical representation and convert to cm.
 * Also updates the inputs with the normalized values.
 */
function normalizeImperialToCm(feetRaw: string, inchesRaw: string): number | null {
  const feet = Number.parseInt(feetRaw, 10);
  const inchesParsed = Number.parseInt(inchesRaw, 10);
  const inches = Number.isNaN(inchesParsed) ? 0 : inchesParsed;

  if (Number.isNaN(feet)) {
    return null;
  }

  // Convert to total inches, avoid negatives
  const totalInches = Math.max(0, feet * 12 + inches);

  // Normalize back to feet + inches (0–11)
  const normFeet = Math.floor(totalInches / 12);
  const normInches = totalInches % 12;

  heightFeet.value = String(normFeet);
  heightInches.value = String(normInches);

  return userUnitToCentimeters({feet: normFeet, inches: normInches});
}

function writeInputsFromCm(cm: number): void {
  const value = clampToRange(cm);
  const converted = centimetersToUserUnit(value);

  if (isImperial) {
    heightFeet.value = String(converted.feet ?? '');
    heightInches.value = String(converted.inches ?? 0);
  } else {
    heightCm.value = String(value);
  }

  setModel(value);
}

watch(
    () => model.value,
    (newValue) => {
      if (internalUpdate) {
        return;
      }

      if (newValue?.numeric !== null && newValue?.numeric !== void 0) {
        writeInputsFromCm(newValue.numeric);
      } else {
        heightFeet.value = '';
        heightInches.value = '';
        heightCm.value = '';
      }
    },
    {immediate: true},
);

function handleBlur(): void {
  touched.value = true;

  if (isImperial) {
    const cm = normalizeImperialToCm(heightFeet.value, heightInches.value);
    if (cm === null) {
      setModel(null);
      return;
    }
    const rounded = roundTo(cm, HEIGHT_DECIMALS);
    setModel(rounded);
  } else {
    if (!heightCm.value?.trim()) {
      setModel(null);
      return;
    }
    const parsed = Number.parseFloat(heightCm.value);
    if (Number.isNaN(parsed)) {
      setModel(null);
      return;
    }
    const rounded = roundTo(parsed, HEIGHT_DECIMALS);
    heightCm.value = String(rounded);
    setModel(rounded);
  }
}

const isValid = computed<boolean>(() => {
  const cm = model.value?.numeric;
  if (cm === null || cm === void 0) {
    return false;
  }

  if (isImperial) {
    const feet = Number.parseInt(heightFeet.value, 10);
    const inches = Number.parseInt(heightInches.value, 10);
    const inchesOk = !Number.isNaN(inches) && inches >= 0 && inches <= 11;
    const feetOk = !Number.isNaN(feet) && feet >= 1;
    return feetOk && inchesOk && cm >= min.value && cm <= max.value;
  }

  return cm >= min.value && cm <= max.value;
});

// Calculate displayable min/max that will actually validate correctly
// Uses only centimetersToUserUnit / userUnitToCentimeters

function snapImperialMin(cmMin: number): {feet: number; inches: number} {
  const converted = centimetersToUserUnit(cmMin);
  let feet = converted.feet ?? 0;
  let inches = converted.inches ?? 0;

  // Move up until the rounded cm value is >= min
  // (guarantees that the displayed min is really in range)
  // With normal height ranges this loop will run only a few times.
   
  while (true) {
    const backToCm = roundTo(userUnitToCentimeters({feet, inches}), HEIGHT_DECIMALS);
    if (backToCm >= cmMin) {
      return {feet, inches};
    }
    inches += 1;
    if (inches === 12) {
      feet += 1;
      inches = 0;
    }
  }
}

function snapImperialMax(cmMax: number): {feet: number; inches: number} {
  const converted = centimetersToUserUnit(cmMax);
  let feet = converted.feet ?? 0;
  let inches = converted.inches ?? 0;

  // Move down until the rounded cm value is <= max
  // (guarantees that the displayed max is really in range)
   
  while (true) {
    const backToCm = roundTo(userUnitToCentimeters({feet, inches}), HEIGHT_DECIMALS);
    if (backToCm <= cmMax) {
      return {feet, inches};
    }
    inches -= 1;
    if (inches < 0) {
      feet -= 1;
      inches = 11;
    }
  }
}

const imperialMin = computed(() => snapImperialMin(min.value));
const imperialMax = computed(() => snapImperialMax(max.value));

const feetMin = computed(() => imperialMin.value.feet);
const feetMax = computed(() => imperialMax.value.feet);
const inchesMin = computed(() => imperialMin.value.inches);
const inchesMax = computed(() => imperialMax.value.inches);

</script>

<template>
  <div class="question-height-input">
    <badge-pill theme="blue" :label="$t('question')"/>
    <div :id="inputLabelledby" v-safe-html="question.name" class="question text-h6"/>

    <div v-if="isImperial" class="input-container">
      <q-input
        v-model="heightFeet"
        class="input input-small"
        type="number"
        inputmode="numeric"
        autocomplete="off"
        :step="1"
        :aria-labelledby="inputLabelledby"
        @blur="handleBlur"
      />
      <span class="unit-label">{{ $t('unit_ft') }}</span>

      <q-input
        v-model="heightInches"
        class="input input-small"
        type="number"
        inputmode="numeric"
        autocomplete="off"
        :step="1"
        :aria-labelledby="inputLabelledby"
        @blur="handleBlur"
      />
      <span class="unit-label">{{ $t('unit_in') }}</span>
    </div>

    <div v-else class="input-container">
      <q-input
        v-model="heightCm"
        class="input"
        type="number"
        inputmode="decimal"
        autocomplete="off"
        :step="HEIGHT_STEP"
        :aria-labelledby="inputLabelledby"
        @blur="handleBlur"
      />
      <span class="unit-label">{{ $t('unit_cm') }}</span>
    </div>

    <form-required
      id="height-validation"
      :valid="isValid"
      :message="isImperial
        ? $t('validation_height_imperial_error_msg', {minFeet: feetMin, minInches: inchesMin, maxFeet: feetMax, maxInches: inchesMax})
        : $t('validation_number_error_msg', {min: min.toFixed(HEIGHT_DECIMALS), max: max.toFixed(HEIGHT_DECIMALS)})"
    />
  </div>
</template>

<style scoped lang="scss">
.question-height-input {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.question {
  margin: var(--Spacing-spacing-2) 0;
  text-align: center;
  max-width: 700px;
  @include font-display-3;
}

.input-container {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.input {
  min-width: 90px;
}

.input :deep(input) {
  text-align: center;
}

.input-small {
  text-align: center;
  min-width: 40px;
  max-width: 60px;
}

.unit-label {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  min-width: 25px;
}
</style>
